import { Platform } from 'react-native';
import { api } from './api';
import type {
  MessageResponse,
  Photo,
  PhotoNoteUpdateResponse,
  PresignedPhotosResponse,
  PresignedPhotoUrl,
  UploadedPhoto,
} from '@exhale/contracts';

export type PhotoUploadInput = {
  uri: string;
  name: string;
  type: string;
  file?: File | { size?: number };
  size?: number;
};

export type { PresignedPhotoUrl } from '@exhale/contracts';

type ApiUploadFile = {
  file?: File;
  uri?: string;
  name: string;
  type: string;
};

const toApiUploadFiles = (photos: PhotoUploadInput[]): ApiUploadFile[] =>
  photos as ApiUploadFile[];

export const chunkPhotosForUpload = (
  photos: PhotoUploadInput[],
  platformOS: string = Platform.OS
): PhotoUploadInput[][] => {
  if (!photos || photos.length === 0) {
    return [];
  }

  const isWeb = platformOS === 'web';
  const maxFilesPerBatch = isWeb ? 1 : 5;
  const maxBatchBytes = isWeb ? 5 * 1024 * 1024 : 50 * 1024 * 1024;

  const batches: PhotoUploadInput[][] = [];
  let currentBatch: PhotoUploadInput[] = [];
  let currentBatchSize = 0;

  const pushBatch = () => {
    if (currentBatch.length > 0) {
      batches.push(currentBatch);
      currentBatch = [];
      currentBatchSize = 0;
    }
  };

  photos.forEach((photo) => {
    const fileSize = photo.file?.size ?? photo.size ?? 0;
    const sizeExceeded = maxBatchBytes > 0 && currentBatchSize + fileSize > maxBatchBytes;
    const countExceeded = maxFilesPerBatch > 0 && currentBatch.length >= maxFilesPerBatch;

    if (currentBatch.length > 0 && (sizeExceeded || countExceeded)) {
      pushBatch();
    }

    currentBatch.push(photo);
    currentBatchSize += fileSize || 0;
  });

  pushBatch();

  return batches;
};

const getUploadEndpoint = (
  reviewId: number,
  responseId: number,
  itemId: number,
  questionId?: string
): string =>
  questionId
    ? `/upload/reviews/${reviewId}/responses/${responseId}/items/${itemId}/questions/${questionId}/photos`
    : `/upload/reviews/${reviewId}/responses/${responseId}/items/${itemId}/photos`;

export const reviewPhotoApi = {
  async getPresignedUrls(photoIds: number[], expiresIn?: number): Promise<PresignedPhotoUrl[]> {
    if (photoIds.length === 0) {
      return [];
    }

    const result = await api.post<PresignedPhotosResponse>('/photos/presign', {
      photoIds,
      expiresIn,
    });

    return result.presigned || [];
  },

  async uploadPhotos(
    reviewId: number,
    responseId: number,
    itemId: number,
    photos: PhotoUploadInput[],
    questionId?: string
  ): Promise<UploadedPhoto[]> {
    const endpoint = getUploadEndpoint(reviewId, responseId, itemId, questionId);
    const batches = chunkPhotosForUpload(photos);
    const uploaded: UploadedPhoto[] = [];

    for (const batch of batches) {
      const batchResult = await api.uploadFiles<UploadedPhoto[]>(endpoint, toApiUploadFiles(batch));
      if (Array.isArray(batchResult)) {
        uploaded.push(...batchResult);
      }
    }

    return uploaded;
  },

  async uploadPhoto(
    reviewId: number,
    itemId: number,
    photoData: PhotoUploadInput
  ): Promise<UploadedPhoto[]> {
    return api.uploadFiles<UploadedPhoto[]>(
      `/reviews/${reviewId}/items/${itemId}/photos`,
      toApiUploadFiles([photoData])
    );
  },

  async deletePhoto(photoId: number): Promise<void> {
    await api.delete<MessageResponse>(`/upload/photos/${photoId}`);
  },

  async updatePhotoNote(photoId: number, note: string): Promise<Photo> {
    return api.patch<PhotoNoteUpdateResponse>(`/upload/photos/${photoId}`, { note });
  },
};
